import {
  isMobile,
  isMantle,
  isAffiliate,
  isAppBaseRoute,
  isCore,
  isCorePaid,
  isWebWinback,
  isEnLanguage,
  isInApp,
  isWinback,
  isEmailMain,
  isIOS,
  isUSWinback,
  isCountry,
  isUS,
  isIntl,
  isDeLanguage,
  isCoreDirect,
  isIntlMantle,
  isUSInAppWinback,
  isAffiliateWithoutTrial,
  isDeEmailMain,
  isEsEmailMain,
  isInAppWinback,
  isHM,
  isDE,
  isDPP,
  isAppAndroidBase,
  isDirect,
  isReferralsBuyflow,
  isCorePaidWithoutAddWordsSEM,
  isAffiliateWithTrial,
  isAU,
  isCA,
  isStrictCore,
  isEsLanguage,
  isAppEmail,
} from ".";
import { routeConstants, SessionStorageKey } from "@utils/constants";
import getStore, { CoreReduxState } from "../redux/store";

import { isEmailWinback, isFromEmail, isUSEmail } from "./email";
import { checkSegments } from "./util";
import {
  EligibleStateCode,
  IneligibleStateCodeForNewPatients,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { getCourseDuration } from "@utils/calculateGoals";
import { getMeristemContext, getSubdivision } from "@utils/meristemContext";
import { getTrackingParams } from "../services/VisitTracker";
import { canRetrieveWeight } from "../refactoredSurvey";
import { getSingleOffers } from "../../hooks/useOffersContext";
import { getSingleAddOns } from "../../hooks/useAddOnsContext";
import { getSingleInAppOffers } from "../../hooks/useInAppOffersContext";
import { appConfig } from "src/config";
import { loadWinbackInfo } from "../redux/slices/winbackInfo";
import { calculateBMIImperial } from "src/primitive/bmi";
import { isAppAndroid, isAppIos, isAppIosBase } from "./in-app";
import {
  isMealPlan,
  isMealWorkoutBundle,
  isWorkoutPlan,
  isNoomSummit,
} from "../addons";
import { isInExperimentConflictingWithSummitCoursePack } from "../api/addons/addons";
import { getIOSMessageHandlers } from "../in-app/messageHandlers";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  FeatureFlags,
  getFeatureFlagValue,
} from "src/pageDefinitions/featureFlags";

import {
  getPromoCodeState,
  isPromoCodePersisted,
} from "../redux/slices/promoCode";
import { TELEHEALTH_FREE_PLAN_ID } from "src/funnels/payment-en/pages/telehealthRedirects";
import { planDurationMonthsToTelehealthNoomPlanId } from "@utils/plansMapping";
import { getNoomSessionStorage } from "@utils/noomSessionStorage";
import { loadActiveSubscriptionDataState } from "../redux/slices/activeSubscriptionData";
import { getMeristemConfigValue } from "../meristemConfig";
import {
  DE_COURSE_PACK_ELIGIBLE_COUNTRIES,
  EN_COURSE_PACK_ELIGIBLE_COUNTRIES,
} from "@utils/userSegment/constants";

const HW_NON_VIP_BATCH_ID_DEV = 1;
const HW_NON_VIP_BATCH_ID_PROD = 354;
export const DPP_OVERRIDE_PLAN = "dpp-12-month";
export const USD_MONTHLY_PLAN_ID = "option_d_20221201_Ex1084var1email";

const devModeFlagSet =
  new URLSearchParams(window.location.search).get("idm") === "true";

export function isDevMode() {
  return devModeFlagSet;
}

export function isPrerender() {
  // eslint-disable-next-line no-underscore-dangle
  return !!(window as any).__prerender;
}

export function evalResultWithFeatureFlag(
  result: boolean,
  flag: keyof FeatureFlags
) {
  const flagValue = getFeatureFlagValue(flag) as boolean;
  if (flagValue === undefined) {
    return result;
  }
  return flagValue;
}

export const isCollapsedPaymentEligible = (state?: CoreReduxState) => {
  return !isInApp(state);
};

export const isHMEligible = () => isUS() && isEnLanguage();

export const isNoomProgramQuestionEligible = (state?: CoreReduxState) =>
  isHMEligible() && isDirect(state) && isIOS();

const hadPreviousITunesSubscription = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // This parameter is set by the growth backend in the internal app endpoint
  // More details about that here: https://github.com/noom/eng-handbook/blob/master/teams/growth/product/Winbacks.md#more-about-in-app-routes
  return urlParams.get("had_itunes_sub") === "true";
};

const disableLPSurveySegment = checkSegments({
  featureFlagOverride: "disableLPSurvey",
});

export const isSurveyOnLPEligible = checkSegments({
  oneOf: [
    routeConstants.exsf01d,
    routeConstants.exsf01,
    routeConstants.affiliateTrialNPB,
    routeConstants.appEmail,
  ],
  noneOf: [isHM, isInApp, disableLPSurveySegment],
});

export const isBraintreeStripeiOSEligible = checkSegments({
  allOf: [hadPreviousITunesSubscription, routeConstants.app14DayReturning],
});

export const isGooglePlayPlansEligible = () => {
  // TODO (Brandon): enable when in-app is ready for release
  // const hasGooglePlayMessageHandler =
  //   !!getAndroidMessageHandlers()?.buyflowPurchaseProduct ||
  //   ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  // return isAppAndroidBase() && hasGooglePlayMessageHandler;
  return false;
};

export const isITunesPlansEligible = () => {
  const hasITunesMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseProduct ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return isAppIos() && hasITunesMessageHandler;
};

export const isITunes1MonthDefaultPlanEligible = () => {
  const hasITunesDefaultPlanMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseNoDetails ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return isAppIos() && hasITunesDefaultPlanMessageHandler;
};

/**
 * Checks if the user can use the native app in-app purchase flow.
 */
export const isInAppNativePurchaseEligible = checkSegments({
  oneOf: [
    isITunes1MonthDefaultPlanEligible,
    isITunesPlansEligible,
    isGooglePlayPlansEligible,
  ],
});

/**
 * Checks if the user can use the web-based in-app purchase flow.
 */
export const isInAppWebPurchaseEligible = checkSegments({
  oneOf: [isAppAndroid, isBraintreeStripeiOSEligible],
});

const ffwdEligibleCurrencies = ["USD", "EUR", "GBP", "CAD", "AUD"];
export const isCurrencyFastForwardEligible = () => {
  const { currency = "USD" } = getStore().getState().serverContext;
  return ffwdEligibleCurrencies.includes(currency);
};

export const isFastForwardEligible = checkSegments({
  allOf: [isEnLanguage, isCurrencyFastForwardEligible],
  noneOf: [isTelehealthPlan],
  oneOf: [
    isCore,
    routeConstants.winback,
    isAppAndroidBase,
    isAffiliateWithTrial,
  ],
});

const isNoomPremiumEligibleUS = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isMantle, isWebWinback, routeConstants.emailMain],
});

export const isNoomPremiumSubscriptionEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
});

export const isFreeSupporterPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isCore, isWebWinback, isEmailMain],
});

const isLanguageNoomPremiumEligible = checkSegments({
  oneOf: [isEnLanguage, isDeLanguage, isEsLanguage],
});

const isNoomPremiumEligibleIntl = checkSegments({
  allOf: [isIntl, isLanguageNoomPremiumEligible],
  oneOf: [
    isMantle,
    isWebWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
});

export const isAddonStoreEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isAppIosBase, isTelehealthPlan],
  oneOf: [
    routeConstants.direct,
    routeConstants.exsf01d,
    routeConstants.exsf01,
    isAffiliateWithoutTrial,
    routeConstants.emailMain,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    isWebWinback,
    isAppAndroidBase,
  ],
});

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [isMobile, isMantle, isFromEmail, isWinback, isInAppWinback],
});

export const userHasPurchasedNoomPremium = () => {
  const { purchasedNoomPremium } = getStore().getState();
  return purchasedNoomPremium;
};

export const isNoomPremiumConsultationSurveyEligible = checkSegments({
  oneOf: [userHasPurchasedNoomPremium, hasBoughtMealAndWorkoutPlan],
});

export const navigateToNoomPremiumConsultationSurvey = checkSegments({
  allOf: [userHasPurchasedNoomPremium],
  oneOf: [
    isMantle,
    routeConstants.affiliateTrialNPB,
    isReferralsBuyflow,
    isEmailWinback,
    isWinback,
    isAppAndroidBase,
  ],
});

export const isImportantDateEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isMantle, isWebWinback, isFromEmail],
});

export const isFivePercentEligible = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [
    isMantle,
    isWinback,
    isUSEmail,
    isDeEmailMain,
    isInAppWinback,
    isUSInAppWinback,
  ],
  allOf: [canRetrieveWeight],
});

export const isFinalGraphEligible = checkSegments({
  oneOf: [isMantle, isUSWinback, isUSEmail, isUSInAppWinback],
});

export const isShowingPaymentGraphExtraContent = checkSegments({
  oneOf: [isMantle, isUSWinback, isInAppWinback],
});

// TODO: Remove isDeLanguage once Noom Premium and Addons can successfully charge SEPA
// https://noomhq.atlassian.net/browse/GT-1256
export const isSEPAEligible = checkSegments({
  allOf: [isDE, isDeLanguage],
});

/**
 * Verifies if the current user should see the final update graph
 */
export const shouldShowFinalUpdateGraph = (state = getStore().getState()) => {
  const surveyAnswers = getSurveyAnswers(state);
  const { weight, idealWeight, weightKg, idealWeightKg } = surveyAnswers;
  const courseDuration = getCourseDuration(surveyAnswers);

  // EnUSBaseline + App14DayBase routes
  if (
    weight &&
    idealWeight &&
    weightKg &&
    idealWeightKg &&
    isFivePercentEligible() &&
    courseDuration <= 8
  ) {
    return true;
  }

  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

function shouldCondenseOrShowFinalUpdateGraph(courseDuration: number) {
  const isCourseDurationEligible =
    courseDuration >= 2 &&
    ((courseDuration <= 8 && isIntlMantle()) || courseDuration <= 6);
  return (
    canRetrieveWeight() && isFinalGraphEligible() && isCourseDurationEligible
  );
}

export const hasNoTrial = checkSegments({
  oneOf: [
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
    routeConstants.ndpp,
    isGooglePlayPlansEligible,
  ],
});

export const isDirectTraffic = checkSegments({
  oneOf: [
    routeConstants.direct,
    routeConstants.appUpsell,
    routeConstants.hmDirect,
  ],
});

export const isTrialFeeWaived = checkSegments({
  oneOf: [
    isInAppWinback,
    isAppIosBase,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    routeConstants.hmReferrals,
    routeConstants.affiliate,
    routeConstants.phillips,
    routeConstants.humana,
  ],
});

export const isChooseTrialPriceNewStyleEligible = checkSegments({
  oneOf: [isMantle, isEmailMain, isHM, isInApp],
});

export const isTrialEligible = (
  { recommendedPlan } = getStore().getState()
) => {
  return recommendedPlan.has_trial && !hasNoTrial();
};

export function showChooseTrialPage(state = getStore().getState()) {
  if (!state.serverContext.trial_fees) {
    return false;
  }
  if (isEnLanguage()) {
    return !isTrialFeeWaived() && !hasNoTrial();
  }
  return (
    isCoreDirect(state) ||
    isCorePaid(state) ||
    isDeEmailMain() ||
    isEsEmailMain() ||
    isHM()
  );
}

export const isPromoCodeBoxEnabled = checkSegments({
  noneOf: [
    isInApp,
    isCorePaidWithoutAddWordsSEM,
    isWinback,
    routeConstants.affiliateNoTrial,
  ],
  featureFlagOverride: "togglePromoCode",
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [isInApp],
});

export const isPaidTraffic = checkSegments({
  oneOf: [
    isCorePaid,
    routeConstants.experiment,
    routeConstants.affiliateTrialNPB,
    routeConstants.hmPaid,
    routeConstants.hmExsf01,
    routeConstants.hmExsf01d,
    routeConstants.hmAwsem,
    routeConstants.winback,
    routeConstants.hmWinback,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [isMantle, isWinback],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [isMantle, isWinback, routeConstants.hmPaid, routeConstants.hmDirect],
});

// How to Turn Off Telehealth: https://github.com/noom/eng-handbook/blob/master/teams/healthcare/playbook/turn_off_telehealth.md
// Check if Telehealth is enabled
export function isTelehealthEnabledGlobally() {
  return !!appConfig.TELEHEALTH_ENABLED;
}

// Used exclusively for users coming through the initial buyflow.
// Determines if the user will see the insurance reimbursement (Telehealth) survey.
export const isEligibleForTelehealthReimbursement = (
  { surveyAnswers } = getStore().getState()
): boolean => {
  const isEligibleState =
    isUS() &&
    getSubdivision() in EligibleStateCode &&
    !(getSubdivision() in IneligibleStateCodeForNewPatients);

  let currentBMI: number;
  const { heightFeet, heightInch, weight } = surveyAnswers;

  if (
    heightFeet !== undefined &&
    heightInch !== undefined &&
    weight !== undefined
  ) {
    currentBMI = calculateBMIImperial(heightFeet, heightInch, weight);
  }

  return (
    isTelehealthEnabledGlobally() &&
    isEligibleState &&
    (isCore() || isReferralsBuyflow() || isAppAndroidBase()) &&
    currentBMI >= 30
  );
};

export function isTelehealthPlan(state = getStore().getState()): boolean {
  const noomPlanId = state?.recommendedPlan?.noom_plan_id;
  const paidTelehealthPlans = Object.values(
    planDurationMonthsToTelehealthNoomPlanId
  );
  return (
    paidTelehealthPlans?.includes(noomPlanId) ||
    noomPlanId === TELEHEALTH_FREE_PLAN_ID
  );
}

const TELEHEALTH_CONTEXT_TYPES = ["telehealth", "telehealth-followup"];
export function isTelehealth() {
  return TELEHEALTH_CONTEXT_TYPES.includes(getMeristemContext().context_type);
}

/**
 * Signals if the current user/experience is subject to HIPPA data protection rules.
 * This can be used to filter logging/etc as appropriate to ensure compliance.
 */
export const isHipaa = (): boolean => {
  return isTelehealth() || isTelehealthPlan();
};

export const isHmSuperswitchEligible = (): boolean => {
  const { activeSubscriptionData } = getStore().getState();
  return (
    isEnLanguage() &&
    activeSubscriptionData.hasActiveSubscription &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug.endsWith("en_US") &&
    ((activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_PROD &&
      appConfig.NOOM_ENV === "production") ||
      activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_DEV)
  );
};

isHmSuperswitchEligible.load = () => {
  const store = getStore();
  const { email } = store.getState().userData;
  if (email) {
    return getStore()
      .dispatch(loadActiveSubscriptionDataState(email))
      .then(() => {});
  }
  return Promise.resolve();
};

export const isEligibleForExitIntentModalEmail = () => {
  return isIntl() && isEmailMain() && getTrackingParams().utm_source === "drip";
};

export function hasPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);
  return !!promoCode?.promoCodeApplied;
}

export function hasVipPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);

  return hasPromoCode() && promoCode?.promoCodeIsVip;
}

export const hasOffers = (state: CoreReduxState) => {
  const { offers } = state;
  return offers.hasOffers;
};
export const isTaxExclusiveCountry = () => isCountry(["US", "CA"]);

export const hasSingleOffers = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleOffers(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleAddOns = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleAddOns(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleInAppOffers = () => {
  const { serverContext, userData } = getStore().getState();
  const singleOffers = getSingleInAppOffers(
    serverContext,
    userData.NoomPremiumSurveyCompleteDateISO
  );

  return singleOffers.length > 0;
};

export const hasEnrollment = () => {
  const { serverContext } = getStore().getState();
  return serverContext?.enrollment;
};

export const isNoomPremiumConsultationSurveyDisqualified = (
  state = getStore().getState()
) => {
  const { surveyAnswers } = state;
  return surveyAnswers?.workoutPlanLimitations?.includes("painOrLosingBreath");
};

export const isNoomPremiumEligible = () => {
  const promoCode = getPromoCodeState();
  const isNoomPremiumEligibleBySegments =
    isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();

  return !promoCode?.promoCodeIsVip && isNoomPremiumEligibleBySegments;
};

export function isEligibleForCheckoutTimer(state = getStore().getState()) {
  return (
    isMantle() && !isTelehealthPlan() && !state.recommendedPlan.isForcedPlan
  );
}

export const requiresDoubleOptInForEmail = () => {
  return isCountry(["DE", "AT"]);
};

export const isWinbackUserAndNotReup = () => {
  const { winbackInfo } = getStore().getState();

  return (
    ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus) &&
    !winbackInfo.willReup
  );
};

isWinbackUserAndNotReup.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isWinbackUser = () => {
  const { winbackInfo } = getStore().getState();

  return ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus);
};

isWinbackUser.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isReUp = () => {
  const { winbackInfo } = getStore().getState();

  return winbackInfo.willReup;
};

isReUp.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const showPromoBox = checkSegments({
  noneOf: [routeConstants.exsf01, routeConstants.exsf01d, isWinback],
});

export function determinePlanOverride() {
  if (isDPP()) {
    return DPP_OVERRIDE_PLAN;
  }
  return "";
}

const isENCoursePackEligible = () => {
  return isCountry(EN_COURSE_PACK_ELIGIBLE_COUNTRIES) && isEnLanguage();
};

export const isDECoursePackEligible = () => {
  return isCountry(DE_COURSE_PACK_ELIGIBLE_COUNTRIES) && isDeLanguage();
};

const isLocaleCoursePackEligible = () => {
  return isENCoursePackEligible() || isDECoursePackEligible();
};

const isRouteCoursePackEligible = checkSegments({
  oneOf: [isCore, isReferralsBuyflow, isEmailMain, isAppAndroidBase, isWinback],
});

export function shouldShowCoursePackOffers() {
  const state = getStore().getState();
  const { recommendedPlan } = state;
  const promoCode = getPromoCodeState(state);
  return (
    isLocaleCoursePackEligible() &&
    isRouteCoursePackEligible() &&
    !isTelehealthPlan() &&
    !promoCode.promoCodeIsVip &&
    recommendedPlan.has_trial &&
    !isReUp()
  );
}

shouldShowCoursePackOffers.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export function acceptedCoursePackOffer() {
  const { checkout, paymentAddon } = getStore().getState();
  return (
    checkout.toggledCoursePackOffer &&
    paymentAddon.hasOffer &&
    shouldShowCoursePackOffers()
  );
}

export const isNoomSummitEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [
    isInExperimentConflictingWithSummitCoursePack,
    isTelehealthPlan,
    isInApp,
  ],
  oneOf: [
    isCore,
    isAffiliate,
    isReferralsBuyflow,
    isWinback,
    isEmailWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
});

export const isNoomSummitDisplayableInStore = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan, hasBoughtNoomSummit],
});

// Users who purchase Noom Summit can still visit the noom-summit-offer pagesets,
// and will be redirected to the confirmation page
export const isNoomSummitPagesetEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan],
});

export function hasBoughtMealPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isMealPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isWorkoutPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtMealAndWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.filter(Boolean).some(isMealWorkoutBundle);
}

export function hasBoughtNoomSummit() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.filter(Boolean).some(isNoomSummit);
}

/**
 * Verifies if current user is on referral routes and wants a full buyflow
 */
export const isFullPersonalizationBuyflow = checkSegments({
  noneOf: [
    ({ surveyAnswers }: CoreReduxState) => {
      return surveyAnswers.customizationChoice?.[0] === "quickStart";
    },
  ],
});

const isPreSelectedPlanLongerThan1Month = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_in_months > 1;
};

const isPreSelectedPlanLongerThan2Months = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_in_months > 2;
};

const isPreSelectedPlanShorterThan11Months = () => {
  const preSelectedRecommendedPlan = getNoomSessionStorage(
    SessionStorageKey.preSelectedRecommendedPlan
  );

  return preSelectedRecommendedPlan?.billing_cycle_in_months < 11;
};

const isPreSelectedPlanTrial14Days = () => {
  const preSelectedRecommendedPlan = getNoomSessionStorage(
    SessionStorageKey.preSelectedRecommendedPlan
  );

  return preSelectedRecommendedPlan?.trial_duration === 14;
};

export const isPlanSelectionEligible = checkSegments({
  allOf: [
    isEmailMain,
    isUS,
    isPreSelectedPlanLongerThan1Month,
    isPreSelectedPlanTrial14Days,
  ],
});

export const shouldShowPlanSelectionSavings = checkSegments({
  allOf: [isPlanSelectionEligible, isPreSelectedPlanLongerThan2Months],
});

export const isLoadingPromoCodeModalEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isEmailMain, isEmailWinback],
});

export const showLongerPlanChoice = checkSegments({
  allOf: [
    isPreSelectedPlanShorterThan11Months,
    isPreSelectedPlanTrial14Days,
    isStrictCore,
  ],
  oneOf: [isCA, isAU],
});

export function shouldRedirectToEmployerSurvey() {
  const { surveyAnswers } = getStore().getState();
  const correctSurveyAnswer =
    surveyAnswers.employmentLifestyle?.includes("fullTime");
  const isCorrectTargeting =
    isEnLanguage() && isUS() && (isCore() || isAppBaseRoute());
  const meristemConfigValue = getMeristemConfigValue(
    "redirectToEmployerSurvey"
  );

  return correctSurveyAnswer && (meristemConfigValue ?? isCorrectTargeting);
}

export function shouldRedirectToHmAddonsContext() {
  return !!getMeristemConfigValue("useHmAddonsContext");
}

export function shouldRedirectHmReferralsContext() {
  return !!getMeristemConfigValue("useHmReferralsContext");
}

export const shouldShowHealthyPaceHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWinback],
});

export const shouldShowImportantEventHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWebWinback],
});

export const isEligibleForNursingOption = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

/**
 * Users can remove promo codes in the following scenarios:
 * - they had a promo code persisted from a previous session
 * - after they remove a persisted promo code, they can remove the following ones
 * - after they apply a promo code and return to the checkout in the same sessions, this includes page refresh, this was
 * deemed and acceptable tradeoff with a PM
 */
export const shouldAllowPromoCodeRemoval = checkSegments({
  allOf: [isPromoCodePersisted, isEnLanguage],
  oneOf: [isEmailMain, isEmailWinback],
});

// ex472b feature. PR: https://github.com/noom/buyflow-client/pull/1356
export const shouldShowAlternatePostCheckoutHeadersText = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const shouldShowUnloadWarning = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const highStressBundle = (state = getStore().getState()) => {
  const {
    manageStress,
    eatImpulsive,
    weighStress,
    selfEsteem,
    holisticHealth,
  } = getSurveyAnswers(state);
  return [
    manageStress,
    eatImpulsive,
    weighStress,
    selfEsteem,
    holisticHealth,
  ].some((answer) => answer && answer[0] >= 4);
};

export const isEligibleForModularBuyflow = checkSegments({
  allOf: [isEnLanguage, isMantle],
  noneOf: [isHM],
});

export const isFreeMonthsOfferEligible = checkSegments({
  allOf: [isUS, isEmailWinback, isEnLanguage],
});

export const hasExistingAcccount = checkSegments({
  oneOf: [isAppEmail],
});
